import React, { RefObject, useEffect, useMemo, useState } from "react";
import { GroupOutlined, Search } from "@mui/icons-material";
import {
  CircularProgress,
  Autocomplete,
  Avatar,
  TextField,
  Typography as T,
  Box,
  Tooltip,
} from "@mui/material";
import useGroups from "admin/hooks/useGroups";
import { useDebounce } from "~/hooks/useDebounce";
import useUsers from "~/pages/admin/hooks/useUsers";
import User, { UserGroup } from "~/pages/admin/types/interfaces/user";

const MIN_CHAR_COUNT = 3;

export enum CollaboratorType {
  USER = "user",
  GROUP = "group",
}

interface UserAutocompleteProps {
  value: {
    uniqueId: string;
    type: CollaboratorType;
    metadata: User | UserGroup;
    label: string;
  } | null;
  onChange: (
    value: {
      uniqueId: string;
      type: CollaboratorType;
      metadata: User | UserGroup;
      label: string;
    } | null
  ) => void;
  placeholder?: string;
  excludedEmails?: string[];
  excludedGroupNames?: string[];
  inputRef?: RefObject<HTMLInputElement | null>;
  setError: (error: string) => void;
  activeOnly?: boolean;
}

interface AutocompleteOption {
  uniqueId: string; // email for users, name for groups
  label: string;
  type: CollaboratorType;
  metadata: User | UserGroup;
  avatarUrl?: string;
}

const UserAndGroupSearchAutocomplete = ({
  value,
  onChange,
  placeholder = `Search by name or group (at least ${MIN_CHAR_COUNT} characters)`,
  excludedEmails = [],
  excludedGroupNames = [],
  inputRef,
  setError,
  activeOnly = false,
}: UserAutocompleteProps) => {
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query.trim());

  const {
    data: groupsData = [],
    isLoading: groupsLoading,
    error: groupsError,
  } = useGroups({
    searchQuery: debouncedQuery,
    enabled: debouncedQuery?.length >= MIN_CHAR_COUNT,
  });
  const {
    data: usersData = [],
    isLoading: usersLoading,
    error: usersError,
  } = useUsers({
    searchQuery: debouncedQuery,
    enabled: debouncedQuery?.length >= MIN_CHAR_COUNT,
    activeOnly,
  });

  const isLoading = usersLoading || groupsLoading;

  useEffect(() => {
    if (groupsError || usersError) {
      setError("Unable to retrieve search results");
    } else {
      setError("");
    }
  }, [groupsError, usersError, setError]);

  const options = useMemo(() => {
    const opts: AutocompleteOption[] = [];
    usersData.forEach((user) => {
      opts.push({
        uniqueId: user.email,
        label: user.full_name || "Unknown",
        type: CollaboratorType.USER,
        avatarUrl: user.image_url,
        metadata: user,
      });
    });
    groupsData.forEach((group) => {
      opts.push({
        uniqueId: group.name,
        label: group.display_name || group.name,
        type: CollaboratorType.GROUP,
        metadata: group,
      });
    });
    return opts;
  }, [usersData, groupsData, debouncedQuery]);

  const filterOptions = (opts: AutocompleteOption[]) => {
    return opts.filter((opt) => {
      if (opt.type === CollaboratorType.USER) {
        return !excludedEmails.includes(opt.uniqueId);
      } else {
        return !excludedGroupNames.includes(opt.uniqueId);
      }
    });
  };

  const clearAutocomplete = () => {
    onChange(null);
    setQuery("");
    inputRef?.current?.focus();
  };

  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement> & { key?: React.Key },
    option: AutocompleteOption
  ) => {
    const isGroup = option.type === "group";

    const content = (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {!isGroup && <Avatar src={option.avatarUrl} sx={{ height: 25, width: 25, mr: 1 }} />}
        {isGroup && (
          <Avatar sx={{ height: 25, width: 25, backgroundColor: "grey.200", mr: 1 }}>
            <GroupOutlined sx={{ fontSize: 16, color: "grey.900" }} />
          </Avatar>
        )}
        <T variant="body2">{option.label}</T>
      </Box>
    );

    return (
      <li {...props} key={option.uniqueId}>
        {!isGroup ? (
          <Tooltip title={option.uniqueId} placement="right" arrow enterDelay={300}>
            {content}
          </Tooltip>
        ) : (
          content
        )}
      </li>
    );
  };

  const renderInput = (params: any) => {
    const { onBlur, ...restInputProps } = params.InputProps;
    return (
      <TextField
        {...params}
        inputRef={inputRef}
        InputProps={{
          ...restInputProps,
          autoFocus: true,
          startAdornment: <Search />,
          endAdornment: (
            <>
              {isLoading && <CircularProgress size={20} />}
              {params.InputProps.endAdornment}
            </>
          ),
          onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
            handleInputBlur();
            onBlur?.(e);
          },
        }}
        placeholder={placeholder}
      />
    );
  };

  const handleChange = (_: unknown, newValue: AutocompleteOption | null) => {
    if (!newValue) {
      clearAutocomplete();
    } else {
      onChange({
        uniqueId: newValue.uniqueId,
        type: newValue.type,
        metadata: newValue.metadata,
        label: newValue.label,
      });
    }
  };

  const handleInputChange = (_: unknown, newInputValue: string) => {
    if (value && newInputValue !== value.label) {
      onChange(null);
    }
    setQuery(newInputValue);
  };

  const handleInputBlur = () => {
    if (value) {
      setQuery(value.label);
    } else {
      setQuery("");
    }
  };

  return (
    <Autocomplete
      blurOnSelect
      clearOnBlur={!value}
      clearOnEscape
      size="small"
      value={value}
      inputValue={value?.label ?? query}
      onInputChange={handleInputChange}
      sx={{
        flex: "1",
        "& .MuiInputBase-sizeSmall.MuiInputBase-sizeSmall": {
          py: "8px",
        },
      }}
      options={options}
      getOptionLabel={(option) => option.label}
      renderOption={renderOption}
      renderInput={renderInput}
      onChange={handleChange}
      filterOptions={filterOptions}
    />
  );
};

export default UserAndGroupSearchAutocomplete;
